/**
 * Organisationsnummer utility functions (frontend twin of the backend's
 * utils/swedishIdValidator.js; mirrors the ssnUtils.js conventions).
 *
 * - Storage format: XXXXXXXXXX (10 digits, no dash) — matches the backend
 *   catalog/tenant convention.
 * - Display format: XXXXXX-XXXX.
 */

/**
 * Normalize an organisationsnummer to 10 digits without dash.
 * Accepts 10 digits with/without dash/spaces, or the 12-digit form with a
 * "16" century prefix.
 *
 * @param {string} orgNumber
 * @returns {string} normalized 10-digit value, or the original input if it
 *                   can't be normalized (will fail validation downstream)
 */
export function normalizeOrgNumber(orgNumber) {
  if (!orgNumber || typeof orgNumber !== 'string') {
    return orgNumber;
  }
  const digits = orgNumber.replace(/\D/g, '');
  if (digits.length === 10) {
    return digits;
  }
  if (digits.length === 12 && digits.startsWith('16')) {
    return digits.substring(2);
  }
  return orgNumber;
}

/**
 * Format an organisationsnummer for display: XXXXXX-XXXX.
 * @param {string} orgNumber - any accepted input form
 * @returns {string}
 */
export function formatOrgNumber(orgNumber) {
  if (!orgNumber) {
    return '';
  }
  const normalized = normalizeOrgNumber(orgNumber);
  if (typeof normalized !== 'string' || !/^\d{10}$/.test(normalized)) {
    return orgNumber;
  }
  return `${normalized.slice(0, 6)}-${normalized.slice(6)}`;
}

/**
 * Validate a Swedish organisationsnummer (10 digits + Luhn checksum).
 * Extracted from NewCompany.js — shared by the company wizard and the
 * supplier/customer forms.
 *
 * @param {string} orgNumber
 * @returns {boolean}
 */
export function validateSwedishOrgNumber(orgNumber) {
  if (!orgNumber || typeof orgNumber !== 'string') {
    return false;
  }
  const cleaned = normalizeOrgNumber(orgNumber);
  if (typeof cleaned !== 'string' || !/^\d{10}$/.test(cleaned)) {
    return false;
  }

  const digits = cleaned.split('').map(Number);
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    let digit = digits[i];
    if (i % 2 === 0) {
      digit *= 2;
      if (digit > 9) {
        digit = digit - 9;
      }
    }
    sum += digit;
  }
  const checkDigit = (10 - (sum % 10)) % 10;
  return checkDigit === digits[9];
}
